import {
  Outlet,
  OutletIdType,
  PrimaryProductAccessType,
  Product,
} from '@collinsonx/utils';
import { useLazyQuery } from '@collinsonx/utils/apollo';
import { Entitlement } from '@collinsonx/utils/generatedTypes/graphql';
import { getProductValuation } from '@collinsonx/utils/queries';
import { convertOutletToProduct } from 'adapters/productAdapter';
import { apiAccountProviderMap } from 'config/constants';
import { useValuationService } from 'config/features';
import { FASTTRACK_IMAGE_URL, FETCH_POLICY } from 'config/productDefaultConfig';
import { getFlight } from 'context/FlightContext';
import { ProductContext } from 'context/ProductContext';
import dayjs from 'dayjs';
import { getBookingOutlet, getBookingProductByCode } from 'gql-queries';
import usePayload from 'hooks/usePayload';
import { useContext, useEffect, useState } from 'react';
import { MappedProductData } from 'types/mappedProductData';
import { replaceTranslationPlaceholder } from 'utils/translations';

import useError from './useError';
import useLocale from './useLocale';

type ProductValuationType = {
  entitlements: Entitlement[];
  priceDetails: {
    currency: string;
    finalPrice: number;
  };
};

const useProduct = () => {
  const [productParsed, setProductParsed] = useState<MappedProductData>();
  const [locationProduct, setLocationProduct] = useState<Product>();

  const {
    consumerID,
    linkedAccountId,
    loungeCode,
    membership,
    payload,
    userIsSignedIn,
  } = usePayload();
  const { product, setProduct } = useContext(ProductContext);
  const { setError } = useError();
  const translation = useLocale();
  const flightData = getFlight();

  const { accountProvider } = payload || {};
  const programme = linkedAccountId ? accountProvider : null;

  const nowTimeLocal = dayjs();

  const departureTimeLocal = dayjs(flightData?.departure?.dateTime?.local);

  const isLocationProduct = loungeCode?.endsWith('FT');

  const canPrebookInMinPeriod =
    departureTimeLocal &&
    nowTimeLocal.isBefore(
      dayjs(departureTimeLocal).subtract(
        product.bookingMinPeriodAllowed,
        'hour'
      )
    );

  const canPrebookingInMaxPeriod =
    departureTimeLocal &&
    dayjs(departureTimeLocal).isBefore(
      nowTimeLocal.add(product.bookingMaxPeriodAllowed, 'hour')
    );

  const validPrebookPeriod = canPrebookInMinPeriod && canPrebookingInMaxPeriod;
  const checkValidCancellationPeriod = (arrivalTime?: string) => {
    if (!arrivalTime) return translation.booking.cancellation.errors.failed;

    if (dayjs(arrivalTime).isBefore(new Date()))
      return translation.booking.cancellation.errors.expiredBooking;

    if (
      nowTimeLocal.isAfter(
        dayjs(arrivalTime).subtract(product.cancellationPeriod, 'hour')
      )
    )
      return replaceTranslationPlaceholder(
        translation.booking.cancellation.errors.notAllowed,
        { time: product.cancellationPeriod }
      ) as string;
  };

  const shouldFetchFroductValuation =
    useValuationService && product.productID && consumerID;

  const [fetchProductValuation, { data: productValuationData }] = useLazyQuery<{
    getProductValuation: ProductValuationType;
  }>(getProductValuation, {
    variables: {
      consumerID,
      membershipID: membership?.id,
      productID: product.productID,
      programme,
    },
  });

  const [fetchOutlet] = useLazyQuery<{
    getOutlet: Outlet;
  }>(getBookingOutlet, {
    fetchPolicy: FETCH_POLICY.networkOnly,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const outletProduct = data?.getOutlet?.products?.filter(
        (outlet) =>
          outlet?.accessType === PrimaryProductAccessType.ReservationFeeOnly
      )[0];

      if (!outletProduct)
        return setError({
          message: translation.generic.error.loungeNoFound,
        });

      const productConfigParsed = convertOutletToProduct(
        data.getOutlet,
        outletProduct
      );

      setProductParsed(productConfigParsed);
    },
    onError: () => {
      setError({
        message: translation.generic.error.loungeNoFound,
      });
    },
  });

  const [fetchLocationProduct] = useLazyQuery<{
    getProductByCode: Product;
  }>(getBookingProductByCode, {
    fetchPolicy: FETCH_POLICY.networkOnly,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const locationProductData = data?.getProductByCode;

      if (!locationProductData)
        return setError({
          message: translation.generic.error.loungeNoFound,
        });

      setLocationProduct(locationProductData);
    },
    onError: () => {
      setError({
        message: translation.generic.error.loungeNoFound,
      });
    },
  });

  useEffect(() => {
    if (!apiAccountProviderMap[accountProvider!]) return;
    if (!userIsSignedIn) return;
    if (product.loungeID !== '' && loungeCode === productParsed?.loungeCode)
      return;

    if (isLocationProduct) {
      fetchLocationProduct({
        variables: {
          code: loungeCode,
          filters: {
            accessType: PrimaryProductAccessType.ReservationFeeOnly,
          },
        },
      });
    } else {
      fetchOutlet({
        variables: {
          accessType: 'RESERVATION_FEE_ONLY',
          additionalFilters: {
            accessType: PrimaryProductAccessType.ReservationFeeOnly,
            programme: accountProvider,
          },
          getOutletId: loungeCode,
          outletIdType: OutletIdType.Code,
          programme: 'PRIORITY_PASS',
        },
      });
    }
  }, [accountProvider, userIsSignedIn, loungeCode]);

  useEffect(() => {
    if (!productParsed) return;

    setProduct(productParsed);
  }, [productParsed]);

  useEffect(() => {
    if (shouldFetchFroductValuation) fetchProductValuation();
  }, [shouldFetchFroductValuation]);

  const productValuation =
    useValuationService && productValuationData?.getProductValuation
      ? {
          currency:
            productValuationData.getProductValuation.priceDetails.currency,
          entitlements: productValuationData.getProductValuation.entitlements,
          saleprice:
            productValuationData.getProductValuation.priceDetails.finalPrice,
        }
      : {};

  const loadingProductValuation =
    shouldFetchFroductValuation && !productValuationData;

  const normalizedProduct =
    isLocationProduct && locationProduct
      ? {
          ...product,
          airportCode: locationProduct.location?.code ?? '',
          airportName: locationProduct.location?.name ?? '',
          category: locationProduct.category ?? '',
          currency: locationProduct.salePrices?.[0]?.salePriceCurrency ?? '',
          imageUrl: FASTTRACK_IMAGE_URL ?? '',
          location: locationProduct.location ?? {},
          loungeCode: locationProduct?.code ?? '',
          loungeID: locationProduct?.id ?? '',
          loungeName: locationProduct.location?.city ?? '',
          partnerID:
            locationProduct?.partnerIntegrationData?.partnerIntegrationID ?? '',
          partnerProductID:
            locationProduct?.partnerIntegrationData?.partnerProductID ?? '',
          productID: locationProduct?.id ?? '',
          saleprice: locationProduct.salePrices?.[0]?.salePrice ?? 0,
          stripeID: locationProduct.salePrices?.[0]?.stripePriceID ?? '',
          terminal: locationProduct.location?.terminal ?? '',
          timezone: locationProduct?.location?.timezone ?? 'UTC',
        }
      : product;

  return {
    ...normalizedProduct,
    ...productValuation,
    checkValidCancellationPeriod,
    fetchProductValuation,
    loadingProduct: isLocationProduct
      ? !locationProduct
      : Boolean(!product.loungeID || loadingProductValuation),
    validPrebookPeriod,
  };
};

export default useProduct;
